import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store/rootReducer";
import { CircularProgress, TextField } from "@mui/material";
import { dateFormatter } from "@/components/DateFormat/DateFormat";
import styles from "../Roles/AuditLogs/AuditLogs.module.scss";
import { useCallback, useEffect, useState, memo } from "react";
import type { AppDispatch } from "@/store/store";
import { getUserGeofenceLogs } from "@/store/slices/GeofenceManagementSlice";
import toast from "@/lib/toast";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useDebounce } from "@/hooks/debounce";


const limit = 10;

const GeofenceLogs = () => {
    const { userGeofencesLogs, loading } = useSelector((state: RootState) => state.usergeofenceManagement);
    const dispatch = useDispatch<AppDispatch>();
    const isLoading = loading.userGeofencesLogs;

    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [hasLogs, setHasLogs] = useState(userGeofencesLogs.length > 0);

    const debounceSearch = useDebounce((searchText || "").trim())

    const getNoteColor = (message: string) => {
        if (message?.toLowerCase().includes("assigned")) return "success";
        if (message?.toLowerCase().includes("removed")) return "error";
        return "";
    };


    const fetchFirstPage = useCallback(async () => {
        setHasNextPage(true);
        try {
            const res: any = await dispatch(getUserGeofenceLogs({ from: 1, to: limit, searchText: debounceSearch, })).unwrap();

            const items = res?.Items ?? [];
            const totalRecords = res?.TotalRecords ?? 0;

            setHasNextPage(items.length < totalRecords);

            if (debounceSearch === "") {
                setHasLogs(totalRecords > 0);
            }

            if (items.length > 0) {
                setPage(2);
            } else {
                setPage(1);
            }
        } catch {
            toast.error("Failed to fetch logs");
        }
    }, [dispatch, debounceSearch]);


    const loadMore = useCallback(async (pageNo: number) => {
        if (isLoading || !hasNextPage) return;

        try {
            const from = (pageNo - 1) * limit + 1;
            const to = pageNo * limit;

            const res: any = await dispatch(getUserGeofenceLogs({ from, to, searchText: debounceSearch })).unwrap();

            const items = res?.Items ?? [];
            const totalRecords = res?.TotalRecords ?? 0;

            const loadedRecords = (pageNo - 1) * limit + items.length;

            setHasNextPage(loadedRecords < totalRecords);

            if (items.length > 0) {
                setPage(pageNo + 1);
            }
        } catch {
            toast.error("Failed to fetch logs");
        }
    }, [dispatch, hasNextPage, isLoading, debounceSearch]);

    const [sentryRef, { rootRef }] = useInfiniteScroll({
        loading: isLoading,
        hasNextPage,
        onLoadMore: () => loadMore(page),
        rootMargin: "0px 0px 200px 0px",
    });

    useEffect(() => {
        fetchFirstPage();
    }, [debounceSearch, fetchFirstPage]);

    return (
        <div className={styles.auditLogContainer}>
            <div className={styles.heading}>
                <h2>Geofence Audit Logs</h2>
            </div>

            {(hasLogs || searchText.trim() !== "") && (
                <div className="flex justify-end p-3">
                    <TextField
                        placeholder="Search"
                        variant="standard"
                        value={searchText}
                        sx={{ width: "50%" }}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>
            )}

            <div
                ref={rootRef}
                className={styles.scrollArea}
            >
                <div className={styles.timelineContainer}>
                    {isLoading && userGeofencesLogs.length === 0 ? (
                        <div className="flex items-center justify-center h-[70vh]" >
                            <CircularProgress size={24} />
                        </div>
                    ) : userGeofencesLogs && userGeofencesLogs.length > 0 ? (
                        <>
                            <div className={styles.timeline}>
                                {userGeofencesLogs.map((log: any) => (
                                    <div
                                        key={log.Id}
                                        className={`${styles.logItem} ${getNoteColor(log.LogMessage) ? styles[getNoteColor(log.LogMessage)] : ""
                                            }`}
                                    >
                                        <div className={styles.details}>
                                            <div className={styles.message}>{log.LogMessage}</div>
                                            <div className={styles.meta}>
                                                {dateFormatter(log.CreateDate, "dd MMM yyyy hh:mm a")} {log.CreatedByName}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {hasNextPage && (
                                    <div ref={sentryRef} style={{
                                        height: 20
                                    }}>
                                        {isLoading && (
                                            <div className="flex items-center justify-center">
                                                <CircularProgress size={22} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center h-[70vh]">
                            No logs found
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default memo(GeofenceLogs);
